import { useCallback, useState } from "react";
import { createViewPropertyCodes } from "@/modules/rev_eu/master_data/views/ViewPropertyCodes";
import type {
    TPropertyCodesQuery,
    TPropertyCodeRow,
} from "@/modules/rev_eu/master_data/views/ViewPropertyCodes";
import { NOOP } from "@/utils/utils";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";

type TPageState = {
    items: TPropertyCodeRow[];
    total: number;
    totalUnfiltered: number;
    isLoading: boolean;
    hasError: boolean;
};

const EMPTY: TPageState = {
    items: [],
    total: 0,
    totalUnfiltered: 0,
    isLoading: true,
    hasError: false,
};

export const wireSettingsPropertyCodes = ({
    store: _store,
    useAppStore,
    api,
}: TCompositionDeps) => {
    const ViewPropertyCodes = createViewPropertyCodes({
        useCases: () => {
            const userPermissions = useAppStore((state) => state.user?.permissions ?? []);
            const [pageState, setPageState] = useState<TPageState>(EMPTY);

            const onQueryChange = useCallback(
                async (query: TPropertyCodesQuery, signal?: AbortSignal) => {
                    setPageState((prev) => ({ ...prev, isLoading: true, hasError: false }));
                    try {
                        const data = await api.propertyCodes.list(
                            {
                                ...query,
                                active_only: false,
                            },
                            signal,
                        );
                        setPageState({
                            items: data.items.map((item) => ({
                                id: item.id,
                                code: item.code,
                                description: item.description,
                                isActive: item.is_active,
                            })),
                            total: data.total,
                            totalUnfiltered: data.total_unfiltered ?? data.total,
                            isLoading: false,
                            hasError: false,
                        });
                    } catch (err) {
                        // Swallow abort errors — the superseding request owns the loading state.
                        if (err instanceof DOMException && err.name === "AbortError") return;
                        setPageState((prev) => ({ ...prev, isLoading: false, hasError: true }));
                    }
                },
                [api],
            );

            return {
                onMount: NOOP,
                onUnmount: NOOP,
                onQueryChange,
                usePropertyCodesPage: () => pageState,
                onCreatePropertyCode: useCallback(
                    async (data: {
                        property_code: string;
                        property_name: string;
                        is_active: boolean;
                    }) => {
                        await api.propertyCodes.create(data);
                    },
                    [api],
                ),
                onUpdatePropertyCode: useCallback(
                    async (
                        id: string,
                        data: { property_code: string; property_name: string; is_active: boolean },
                    ) => {
                        await api.propertyCodes.update(id, data);
                    },
                    [api],
                ),
                onDeletePropertyCode: useCallback(
                    async (id: string) => {
                        await api.propertyCodes.delete(id);
                    },
                    [api],
                ),
                onLoadPropertyCodeUsage: useCallback(
                    async (id: string) => {
                        const usage = await api.propertyCodes.usage(id);
                        return { mappingCount: usage.mapping_count, usedBy: usage.used_by };
                    },
                    [api],
                ),
                onUploadFile: useCallback(
                    async (file: File) => api.propertyCodes.upload(file),
                    [api],
                ),
                permissions: () => userPermissions,
            };
        },
    });

    return { ViewPropertyCodes };
};
